import _ from 'lodash-es';
import classNames from 'classnames';
import intl from 'react-intl-universal';

import request from '../../../libs/request';
import CountryData from './CountryData.js';

import './style.css'

export default class PhoneInput {

    static defaultOptions = {
        country: '',
        value: '',

        onlyCountries: [],
        preferredCountries: [],
        excludeCountries: [],

        placeholder: '1 (702) 123-4567',
        searchPlaceholder: 'search',
        searchNotFound: 'No entries to show',
        flagsImagePath: './flags.png',
        disabled: false,

        containerStyle: "",
        inputStyle: "",
        buttonStyle: "",
        dropdownStyle: "",
        searchStyle: "",

        containerClass: '',
        inputClass: '',
        buttonClass: '',
        dropdownClass: '',
        searchClass: '',
        className: '',

        autoFormat: true,
        enableAreaCodes: false,
        enableTerritories: false,
        disableCountryCode: false,
        disableDropdown: false,
        enableLongNumbers: false,
        countryCodeEditable: true,
        enableSearch: false,
        disableSearchIcon: false,
        disableInitialCountryGuess: false,
        disableCountryGuess: false,

        regions: '',

        inputProps: {},
        localization: {},

        masks: null,
        priority: null,
        areaCodes: null,

        preserveOrder: [],

        defaultMask: '... ... ... ... ..', // prefix+dialCode+' '+defaultMask
        alwaysDefaultMask: false,
        prefix: '+',
        copyNumbersOnly: true,
        renderStringAsFlag: '',
        autocompleteSearch: false,
        jumpCursorToEnd: true,
        enableAreaCodeStretch: false,
        enableClickOutside: true,
        showDropdown: false,

        isValid: true, // (value, selectedCountry, onlyCountries, hiddenAreaCodes) => true | false | 'Message'
        defaultErrorMessage: '',
        specialLabel: 'Phone',

        onEnterKeyPress: null, // null or function

        keys: {
            UP: 38, DOWN: 40, RIGHT: 39, LEFT: 37, ENTER: 13,
            ESC: 27, PLUS: 43, A: 65, Z: 90, SPACE: 32, TAB: 9,
        }
    };

    constructor(props) {
        props = _.assignIn(PhoneInput.defaultOptions, props);
        this.props = props;

        const { onlyCountries, preferredCountries, hiddenAreaCodes } = new CountryData(
            props.enableAreaCodes, props.enableTerritories, props.regions,
            props.onlyCountries, props.preferredCountries, props.excludeCountries, props.preserveOrder,
            props.masks, props.priority, props.areaCodes, props.localization,
            props.prefix, props.defaultMask, props.alwaysDefaultMask,
        );

        const inputNumber = props.value ? props.value.replace(/\D/g, '') : '';

        let countryGuess;
        if (props.disableInitialCountryGuess) {
            countryGuess = 0;
        } else if (inputNumber.length > 1) {
            // Country detect by phone
            countryGuess = this.guessSelectedCountry(inputNumber.substring(0, 6), props.country, onlyCountries, hiddenAreaCodes) || 0;
        } else if (props.country) {
            // Default country
            countryGuess = onlyCountries.find(o => o.iso2 === props.country) || 0;
        } else {
            // Empty params
            countryGuess = 0;
        }

        const dialCode = (
            inputNumber.length < 2 &&
            countryGuess &&
            !_.startsWith(inputNumber, countryGuess.dialCode)
        ) ? countryGuess.dialCode : '';

        let formattedNumber;
        formattedNumber = (inputNumber === '' && countryGuess === 0) ? '' :
        this.formatNumber(
            (props.disableCountryCode ? '' : dialCode) + inputNumber,
            countryGuess.name ? countryGuess : undefined
        );

        const highlightCountryIndex = onlyCountries.findIndex(o => o === countryGuess);

        this.state = {
            showDropdown: props.showDropdown,

            formattedNumber,
            onlyCountries,
            preferredCountries,
            hiddenAreaCodes,
            selectedCountry: countryGuess,
            highlightCountryIndex,

            queryString: '',
            freezeSelection: false,
            searchValue: '',
        };

        this.element = null;
    }

    getInputField = () => {
        return document.getElementById(this.props.id).querySelector('.number-input');
    }

    getProbableCandidate = _.memoize((queryString) => {
        if (!queryString || queryString.length === 0) {
            return null;
        }
        // don't include the preferred countries in search
        const probableCountries = this.state.onlyCountries.filter((country) => {
            return _.startsWith(country.name.toLowerCase(), queryString.toLowerCase());
        }, this);
        return probableCountries[0];
    });

    guessSelectedCountry = _.memoize((inputNumber, country, onlyCountries, hiddenAreaCodes) => {
        // if enableAreaCodes == false, try to search in hidden area codes to detect area code correctly
        // then search and insert main country which has this area code
        // https://github.com/bl00mber/react-phone-input-2/issues/201
        if (this.props.enableAreaCodes === false) {
            let mainCode;
            hiddenAreaCodes.some(country => {
                if (_.startsWith(inputNumber, country.dialCode)) {
                    // eslint-disable-next-line array-callback-return
                    onlyCountries.some(o => {
                        if (country.iso2 === o.iso2 && o.mainCode) {
                            mainCode = o;
                            return true;
                        }
                    })
                    return true;
                }
                return false;
            })
            if (mainCode) return mainCode;
        }

        const secondBestGuess = onlyCountries.find(o => o.iso2 === country);
        if (inputNumber.trim() === '') return secondBestGuess;

        const bestGuess = onlyCountries.reduce((selectedCountry, country) => {
            if (_.startsWith(inputNumber, country.dialCode)) {
                if (country.dialCode.length > selectedCountry.dialCode.length) {
                    return country;
                }
                if (country.dialCode.length === selectedCountry.dialCode.length && country.priority < selectedCountry.priority) {
                    return country;
                }
            }
            return selectedCountry;
        }, {dialCode: '', priority: 10001}, this);

        if (!bestGuess.name) return secondBestGuess;
        return bestGuess;
    });

    // View methods
    scrollTo = (country, middle) => {
        if (!country) return;
        const container = this.element.querySelector("[ref='countryListRef']");
        if (!container || !document.body) return;

        const containerHeight = container.offsetHeight;
        const containerOffset = container.getBoundingClientRect();
        const containerTop = containerOffset.top + document.body.scrollTop;
        const containerBottom = containerTop + containerHeight;

        const element = country;
        const elementOffset = element.getBoundingClientRect();

        const elementHeight = element.offsetHeight;
        const elementTop = elementOffset.top + document.body.scrollTop;
        const elementBottom = elementTop + elementHeight;

        let newScrollTop = elementTop - containerTop + container.scrollTop;
        const middleOffset = (containerHeight / 2) - (elementHeight / 2);
        if (this.props.enableSearch ? elementTop < containerTop + 32 : elementTop < containerTop) {
            // scroll up
            if (middle) {
                newScrollTop -= middleOffset;
            }
            container.scrollTop = newScrollTop;
        } else if (elementBottom > containerBottom) {
            // scroll down
            if (middle) {
                newScrollTop += middleOffset;
            }
            const heightDifference = containerHeight - elementHeight;
            container.scrollTop = newScrollTop - heightDifference;
        }
    };

    formatNumber = (text, country) => {
        if (!country) return text;

        const { format } = country;
        const { disableCountryCode, enableAreaCodeStretch, enableLongNumbers, autoFormat } = this.props;

        let pattern;
        if (disableCountryCode) {
            pattern = format.split(' ');
            pattern.shift();
            pattern = pattern.join(' ');
        } else {
            if (enableAreaCodeStretch && country.isAreaCode) {
                pattern = format.split(' ');
                pattern[1] = pattern[1].replace(/\.+/, ''.padEnd(country.areaCodeLength, '.'))
                pattern = pattern.join(' ');
            } else {
                pattern = format;
            }
        }

        if (!text || text.length === 0) {
            return disableCountryCode ? '' : this.props.prefix;
        }

        // for all strings with length less than 3, just return it (1, 2 etc.)
        // also return the same text if the selected country has no fixed format
        if ((text && text.length < 2) || !pattern || !autoFormat) {
            return disableCountryCode ? text : this.props.prefix+text;
        }

        const formattedObject = _.reduce(pattern, (acc, character) => {
            if (acc.remainingText.length === 0) {
                return acc;
            }

            if (character !== '.') {
                return {
                    formattedText: acc.formattedText + character,
                    remainingText: acc.remainingText
                };
            }

            const [ head, ...tail ] = acc.remainingText;

            return {
                formattedText: acc.formattedText + head,
                remainingText: tail
            };
        }, {
            formattedText: '',
            remainingText: text.split('')
        });

        let formattedNumber;
        if (enableLongNumbers) {
            formattedNumber = formattedObject.formattedText + formattedObject.remainingText.join('');
        } else {
            formattedNumber = formattedObject.formattedText;
        }

        // Always close brackets
        if (formattedNumber.includes('(') && !formattedNumber.includes(')')) formattedNumber += ')';
        return formattedNumber;
    }

    handleFlagItemClick = (countryCode, ref) => {
        const newSelectedCountry = this.state.onlyCountries.find(o => o.iso2 === countryCode);
        if (!newSelectedCountry) return;

        const currentSelectedCountry = this.state.selectedCountry;
        this.state.selectedCountry = newSelectedCountry;

        const unformattedNumber = this.state.formattedNumber.replace(' ', '').replace('(', '').replace(')', '').replace('-', '');
        const newNumber = unformattedNumber.length > 1 ? unformattedNumber.replace(currentSelectedCountry.dialCode, newSelectedCountry.dialCode) : newSelectedCountry.dialCode;
        const formattedNumber = this.formatNumber(newNumber.replace(/\D/g, ''), newSelectedCountry);

        var inputEle = this.getInputField();
        inputEle.value = formattedNumber;

        var flagEle = document.getElementById(this.props.id).querySelector('.flag');
        flagEle.className = "flag " + newSelectedCountry.iso2;

        this.state.formattedNumber = formattedNumber;
    }

    getElement = (index) => {
        return this.element.querySelector(`li[data-flag-key="flag_no_${index}"]`);
    }

    handleFlagDropdownClick = (e) => {
        e.preventDefault();

        if (!this.state.showDropdown && this.props.disabled) return;
        const { preferredCountries, onlyCountries, selectedCountry } = this.state;
        const allCountries = this.concatPreferredCountries(preferredCountries, onlyCountries);

        const highlightCountryIndex = allCountries.findIndex(o =>
            o.dialCode === selectedCountry.dialCode && o.iso2 === selectedCountry.iso2);
        this.state.highlightCountryIndex = highlightCountryIndex;
        var selectedEl = this.getElement(this.state.highlightCountryIndex);
        this.element.querySelectorAll("[ref='countryRef']").forEach(el => {
            el.classList.remove('highlight');
        });
        selectedEl.classList.add('highlight');
        this.scrollTo(selectedEl);
    }

    handleInput = (e) => {
        const inputEle = this.getInputField();

        var { value } = e.target;
        const { prefix, onChange } = this.props;

        let formattedNumber = this.props.disableCountryCode ? '' : prefix;
        let newSelectedCountry = this.state.selectedCountry;
        let freezeSelection = this.state.freezeSelection;

        if (!this.props.countryCodeEditable) {
            const mainCode = newSelectedCountry.hasAreaCodes ?
            this.state.onlyCountries.find(o => o.iso2 === newSelectedCountry.iso2 && o.mainCode).dialCode :
            newSelectedCountry.dialCode;

            const updatedInput = prefix+mainCode;
            if (value.slice(0, updatedInput.length) !== updatedInput) return;
        }

        if (value === prefix) {
            // we should handle change when we delete the last digit
            if (onChange) onChange('', this.getCountryData(), e, '');
            this.state.formattedNumber = "";
            inputEle.value = this.state.formattedNumber;
        }

        // Does exceed default 15 digit phone number limit
        if (value.replace(/\D/g, '').length > 15) {
            if (this.props.enableLongNumbers === false) return;
            if (typeof this.props.enableLongNumbers === 'number') {
                if (value.replace(/\D/g, '').length > this.props.enableLongNumbers) return;
            }
        }

        // if the input is the same as before, must be some special key like enter etc.
        if (value === this.state.formattedNumber) return;
/*
        // ie hack
        if (e.preventDefault) {
            e.preventDefault();
        } else {
            e.returnValue = false;
        }
*/
        const { country } = this.props
        const { onlyCountries, selectedCountry, hiddenAreaCodes } = this.state

        if (onChange) e.persist();

        if (value.length > 0) {
            // before entering the number in new format, lets check if the dial code now matches some other country
            const inputNumber = value.replace(/\D/g, '');

            // we don't need to send the whole number to guess the country... only the first 6 characters are enough
            // the guess country function can then use memoization much more effectively since the set of input it
            // gets has drastically reduced
            if (!this.state.freezeSelection || (!!selectedCountry && selectedCountry.dialCode.length > inputNumber.length)) {
                if (this.props.disableCountryGuess) {newSelectedCountry = selectedCountry;}
                else {
                    newSelectedCountry = this.guessSelectedCountry(inputNumber.substring(0, 6), country, onlyCountries, hiddenAreaCodes) || selectedCountry;
                }
                freezeSelection = false;
            }
            formattedNumber = this.formatNumber(inputNumber, newSelectedCountry);
            newSelectedCountry = newSelectedCountry.dialCode ? newSelectedCountry : selectedCountry;
        }

        const oldCaretPosition = e.target.selectionStart;
        let caretPosition = e.target.selectionStart;
        const oldFormattedText = this.state.formattedNumber;
        const diff = formattedNumber.length - oldFormattedText.length;

        this.state.formattedNumber = formattedNumber;
        inputEle.value = formattedNumber;

        this.state.freezeSelection = freezeSelection;

        this.state.selectedCountry = newSelectedCountry;
        var flagEle = document.getElementById(this.props.id).querySelector('.flag');
        flagEle.className = "flag " + newSelectedCountry.iso2;

        if (diff > 0) {
            caretPosition = caretPosition - diff;
        }

        const lastChar = formattedNumber.charAt(formattedNumber.length - 1);

        if (lastChar === ')') {
            inputEle.setSelectionRange(formattedNumber.length - 1, formattedNumber.length - 1);
        } else if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
            inputEle.setSelectionRange(caretPosition, caretPosition);
        } else if (oldCaretPosition < oldFormattedText.length) {
            inputEle.setSelectionRange(oldCaretPosition, oldCaretPosition);
        }

        if (onChange) onChange(formattedNumber.replace(/[^0-9]+/g,''), this.getCountryData(), e, formattedNumber);
    }

    searchCountry = () => {
        const probableCandidate = this.getProbableCandidate(this.state.queryString) || this.state.onlyCountries[0];
        const probableCandidateIndex = this.state.onlyCountries.findIndex(o => o === probableCandidate) + this.state.preferredCountries.length;

        this.scrollTo(this.getElement(probableCandidateIndex), true);
    }

    concatPreferredCountries = (preferredCountries, onlyCountries) => {
        if (preferredCountries.length > 0) { return [...new Set(preferredCountries.concat(onlyCountries))] }
        else { return onlyCountries }
    }

    getDropdownCountryName = (country) => {
        return country.localName || country.name;
    }

    getSearchFilteredCountries = () => {
        const { preferredCountries, onlyCountries, searchValue } = this.state
        const { enableSearch } = this.props
        const allCountries = this.concatPreferredCountries(preferredCountries, onlyCountries);
        const sanitizedSearchValue = searchValue.trim().toLowerCase().replace('+','');
        if (enableSearch && sanitizedSearchValue) {
            // [...new Set()] to get rid of duplicates
            // firstly search by iso2 code
            if (/^\d+$/.test(sanitizedSearchValue)) { // contains digits only
                // values wrapped in ${} to prevent undefined
                return allCountries.filter(({ dialCode }) =>
                    [`${dialCode}`].some(field => field.toLowerCase().includes(sanitizedSearchValue)))
            } else {
                const iso2countries = allCountries.filter(({ iso2 }) =>
                    [`${iso2}`].some(field => field.toLowerCase().includes(sanitizedSearchValue)))
                // || '' - is a fix to prevent search of 'undefined' strings
                // Since all the other values shouldn't be undefined, this fix was accepte
                // but the structure do not looks very good
                const searchedCountries = allCountries.filter(({ name, localName, iso2 }) =>
                    [`${name}`, `${localName || ''}`].some(field => field.toLowerCase().includes(sanitizedSearchValue)))
                this.scrollToTop()
                return [...new Set([].concat(iso2countries, searchedCountries))]
            }
        } else {
            return allCountries
        }
    }

    hideCountryList = (event) => {
        var flagDropdownEl = this.element.querySelector("[ref='flagDropdownRef']");
        var countryListEl = this.element.querySelector("[ref='countryListRef']");
        if (!flagDropdownEl) {
            return;
        }
        const isClickInside = flagDropdownEl.contains(event.target);
        if (!isClickInside) {
            this.state.showDropdown = false;
            countryListEl.classList.add('hide');
        }
    }

    attachCountryItemEvents = () => {
        var countryListEl = this.element.querySelector("[ref='countryListRef']");
        var countryEls = this.element.querySelectorAll("[ref='countryRef']");

        countryEls.forEach((countryEl) => {
            countryEl.addEventListener('click', event => {
                var ele = event.target;
                if (!ele.classList.contains("country")) {
                    ele = event.target.parentElement;
                }
                var countryCode = ele.getAttribute('data-country-code');
                this.handleFlagItemClick(countryCode);
                this.state.showDropdown = false;
                countryListEl.classList.add('hide');
            });
        });
    }

    customErrorMessageForPhoneValidation = (validationResult) => {
        if (!validationResult) return null;
        var errorMessage = null;
        if (validationResult.opted_out) {
            var servicePhoneNumber = _.get(window.CONFIG, 'Settings.message_service.from');
            errorMessage = intl.get("phone_err_opt_out_v", {val: servicePhoneNumber}).d('This number has opted out of receiving SMS from VSee. If you would like VSee to send visit updates to this person via SMS, please enter a different number or ask this person to opt in by texting START to {val}.');;
        } else if(validationResult.voip) {
            errorMessage = intl.get("phone_err_voip").d('VoIP number may not be able to receive SMS.');
        } else if (validationResult.unreachable) {
            errorMessage = intl.get("phone_err_unreachable").d("Unreachable carrier won't be able to receive SMS. If you would like VSee to send visit updates to this person via SMS, please enter a valid mobile number.")
        } else if (validationResult.landline) {
            errorMessage = intl.get("phone_err_landline").d("Landline number won't be able to receive SMS. If you would like VSee to send visit updates to this person via SMS, please enter a valid mobile number.");
        }
        return errorMessage;
    }

    validate = async (number) => {
        var response = await request.post(`${window.base_url}/ms/phones/validate`, "phones=" + encodeURIComponent(number));
        var simplifiedNum = number.replace(/\s|\(|\)|-/g, "");
        var data = _.get(response, "data." + simplifiedNum);
        var errMsg = this.customErrorMessageForPhoneValidation(data);
        var errEl = this.element.querySelector(".opted-out-error");
        var errMsgEl = this.element.querySelector(".opted-out-error-msg");
        if (errMsg) {
            errMsgEl.innerHTML = errMsg;
            errEl.classList.add("has-error");
        } else {
            errMsgEl.innerHTML = "";
            errEl.classList.remove("has-error");
        }
    }

    attachEvents = (element, callback) => {
        this.element = element;

        var inputEl = element.querySelector("[ref='inputRef']");
        var flagDropdownEl = element.querySelector("[ref='flagDropdownRef']");
        var countryListEl = element.querySelector("[ref='countryListRef']");

        document.addEventListener('click', this.hideCountryList);

        flagDropdownEl.addEventListener('click', event => {
            this.state.showDropdown = true;
            countryListEl.classList.remove('hide');
            this.handleFlagDropdownClick(event);
        });

        inputEl.addEventListener('keydown', event => {
            this.handleInput(event);
        });

        inputEl.addEventListener('change', event => {
            this.handleInput(event);
            this.validate(this.state.formattedNumber);
            if (callback) {
                callback("setValue", this.state.formattedNumber);
            }
        });

        this.attachCountryItemEvents();
    }

    removeEvents = () => {
        this.element = null;
        document.removeEventListener('click', this.hideCountryList);
    }

    getCountryDropdownList = () => {
        const { preferredCountries, highlightCountryIndex, showDropdown, searchValue } = this.state;
        const { disableDropdown, prefix } = this.props
        const { enableSearch, searchNotFound, disableSearchIcon, searchClass, searchStyle, searchPlaceholder, autocompleteSearch } = this.props;
    
        const searchedCountries = this.getSearchFilteredCountries()
    
        let countryDropdownList = searchedCountries.map((country, index) => {
            const highlight = highlightCountryIndex === index;
            const itemClasses = classNames({
                country: true,
                preferred: country.iso2 === 'us' || country.iso2 === 'gb',
                active: country.iso2 === 'us',
                highlight
            });

            const inputFlagClasses = `flag ${country.iso2}`;

            const countryEle = 
                "<li" +
                    " data-flag-key='flag_no_" + index + "'" +
                    " class='" + itemClasses + "'" +
                    " data-dial-code='1'" +
                    " tabIndex='" + (disableDropdown ? '-1' : '0') + "'" +
                    " data-country-code='" + country.iso2 + "'" +
                    " role='option'" +
                    (highlight ? " aria-selected='true'" : "") +
                    " ref=countryRef" +
                ">" +
                    "<div class='" + inputFlagClasses + "' ></div>" +
                    "<span class='country-name'>" + this.getDropdownCountryName(country) + "</span>" +
                    "<span class='dial-code'>" +
                        (country.format ? this.formatNumber(country.dialCode, country) : (prefix + country.dialCode)) +
                    "</span>" +
                "</li>";

            return countryEle;
        });

        const dashedLi = (<li key={'dashes'} className='divider'/>);
        // let's insert a dashed line in between preffered countries and the rest
        (preferredCountries.length > 0) && (!enableSearch || (enableSearch && !searchValue.trim())) &&
            countryDropdownList.splice(preferredCountries.length, 0, dashedLi);

        const dropDownClasses = classNames({
            'country-list': true,
            'hide': !showDropdown,
            [this.props.dropdownClass]: true,
        });

        const countryListEle =
            "<ul" +
                " class='" + dropDownClasses + "'" +
                " style='" + this.props.dropdownStyle + "'" +
                " role='listbox'" +
                " tabIndex='0'" +
                " display='" + (showDropdown ? 'block' : 'none') + "'" +
                " ref='countryListRef'" +
            ">" +
                (enableSearch ? (
                    "<li class='" + classNames({search: true, [searchClass]: searchClass}) + "' >" +
                        (!disableSearchIcon ? (
                            "<span" +
                                " class='" + classNames({'search-emoji': true, [`${searchClass}-emoji`]: searchClass}) + "'" +
                                " role='img'" +
                                " aria-label='Magnifying glass'" +
                            ">" +
                                "&#128270;" +
                            "</span>"
                        ) : "") +
                        "<input" +
                            " class='" + classNames({'search-box': true, [`${searchClass}-box`]: searchClass}) + "'" +
                            " style='" + searchStyle + "'" +
                            " type='search'" +
                            " placeholder='" + searchPlaceholder + "'" +
                            " auto-focus='true'" +
                            " auto-complete='" + (autocompleteSearch ? 'on' : 'off') + "'" +
                            " value='" + searchValue + "'" +
                        "/>" +
                    "</li>"
                ) : "") +
                (countryDropdownList.length > 0 ? countryDropdownList.join("") : (
                    "<li class='no-entries-message'>" +
                        "<span>" + searchNotFound + "</span>" +
                    "</li>"
                )) +
            "</ul>";
        return countryListEle;
    }

    render(children) {
        const { onlyCountries, selectedCountry, showDropdown, formattedNumber, hiddenAreaCodes } = this.state;
        const { disableDropdown, isValid, defaultErrorMessage, specialLabel } = this.props;

        let isValidValue, errorMessage;
        if (typeof isValid === 'boolean') {
            isValidValue = isValid;
        } else {
            const isValidProcessed = isValid(formattedNumber.replace(/\D/g, ''), selectedCountry, onlyCountries, hiddenAreaCodes)
            if (typeof isValidProcessed === 'boolean') {
                isValidValue = isValidProcessed;
                if (isValidValue === false) errorMessage = defaultErrorMessage
            } else { // typeof === 'string'
                isValidValue = false;
                errorMessage = isValidProcessed;
            }
        }

        const containerClasses = classNames({
            [this.props.containerClass]: true,
            'react-tel-input': true,
        });
        const arrowClasses = classNames({'arrow': true, 'up': showDropdown});
        const inputClasses = classNames({
            'form-control': true,
            'number-input': true,
            'invalid-number': !isValidValue,
            'open': showDropdown
        });
        const selectedFlagClasses = classNames({
            'selected-flag': true,
            'open': showDropdown,
        });
        const flagViewClasses = classNames({
            'flag-dropdown': true,
            'invalid-number': !isValidValue,
            'open': showDropdown
        });
        const inputFlagClasses = `flag ${selectedCountry && selectedCountry.iso2}`;
        const html =
            "<div class='" + containerClasses + "' " + 
                (this.props.id ? "id='" + this.props.id + "'" : "") +
                ">" +
                "<div style='position:relative'>" +
                    (specialLabel ? "<div class='special-label'>" + specialLabel + "</div>" : "") +
                    (errorMessage ? "<div class='invalid-number-message'>" + errorMessage + "</div>" : "") + 
                    "<input" +
                        " class='" + inputClasses + "'" +
                        " style='" + this.props.inputStyle + "'" +
                        " value='" + formattedNumber + "'" +
                        " name='data[" + this.props.key + "]'" +
                        " placeholder='" + this.props.placeholder + "'" +
                        (this.props.disabled ? " disabled='true'" : "") +
                        " type='tel'" +
                        " ref='inputRef'" +
                    "/>" +
                    "<div" +
                        " class='" + flagViewClasses + "'" +
                        " style='" + this.props.buttonStyle + "'" +
                        " ref='flagDropdownRef'" +
                    ">" +
                        "<div" +
                            " class='" + selectedFlagClasses + "'" +
                            " title='" + (selectedCountry ? ((selectedCountry.localName || selectedCountry.name) + ": + " + selectedCountry.dialCode) : "") + "'" +
                            " tabIndex='" + (disableDropdown ? '-1' : '0') + "'" +
                            " role='button'" +
                            " aria-haspopup='listbox'" +
                            " aria-expanded='" + (showDropdown ? true : undefined) + "'" +
                        ">" +
                            "<div class='" + inputFlagClasses + "' data-testid='flagDiv'>" +
                                (!disableDropdown ? "<div class='" + arrowClasses + "' ></div>" : "") +
                            "</div>" +
                        "</div>" +
                    "</div>" +
                    this.getCountryDropdownList() +
                "</div>" +
                "<div class='opted-out-error'>" +
                    "<div class='opted-out-error-msg'></div>" +
                "</div>" +
            "</div>";
        return html;
    }
}